import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	Divider,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import type {
	ProductHeader,
	ProductSizeOption,
	ProductTypeOption,
} from "../models/MillSetupSheet";

interface Props {
	productTypes: ProductTypeOption[];
	productSizes: ProductSizeOption[];
	selectedTypeId: number | null;
	selectedSizeId: number | null;
	header: ProductHeader | null;
	loadingSizes: boolean;
	onTypeChange: (id: number | null) => void;
	onSizeChange: (id: number | null) => void;
	onClear: () => void;
}

const HeaderField = ({ label, value }: { label: string; value: string }) => (
	<Box sx={{ display: "flex", flexDirection: "column" }}>
		<Typography
			sx={{ color: "text.secondary", fontSize: "0.7rem", lineHeight: 1.2 }}
		>
			{label}
		</Typography>
		<Typography sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.2 }}>
			{value || "-"}
		</Typography>
	</Box>
);

export const MillSetupSheetToolbar = ({
	productTypes,
	productSizes,
	selectedTypeId,
	selectedSizeId,
	header,
	loadingSizes,
	onTypeChange,
	onSizeChange,
	onClear,
}: Props) => {
	// Narrow the Product list to the selected Product Type. The legacy SP
	// (CAT.GetProductSizes) has no type parameter, so in legacy mode the proxy
	// returns every product size — we must filter client-side (same convention
	// as CobblesReportToolbar). Rows carry ProductTypeId in both flag modes.
	const filteredSizes =
		selectedTypeId == null
			? productSizes
			: productSizes.filter((s) => s.ProductTypeId === selectedTypeId);

	const selectedSize =
		filteredSizes.find((s) => s.Id === selectedSizeId) ?? null;

	const revisionDate = header?.RevisionTimestampString
		? header.RevisionTimestampString
		: header?.RevisionTimestamp
			? dxtToLocalServerTime(header.RevisionTimestamp, "yyyy-MM-dd")
			: "";

	return (
		<Paper
			elevation={3}
			sx={{
				px: 1.5,
				py: 0.75,
				mb: 1,
				display: "flex",
				gap: 1,
				alignItems: "center",
				flexWrap: "wrap",
			}}
		>
			<FormControl size="small" margin="dense" sx={{ minWidth: 200 }}>
				<InputLabel id="mss-product-type-label">Product Type</InputLabel>
				<Select
					labelId="mss-product-type-label"
					label="Product Type"
					value={selectedTypeId ?? ""}
					onChange={(e) =>
						onTypeChange(e.target.value === "" ? null : Number(e.target.value))
					}
				>
					{productTypes.map((t) => (
						<MenuItem key={t.Id} value={t.Id}>
							{t.Name}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<Autocomplete
				size="small"
				sx={{ minWidth: 240 }}
				options={filteredSizes}
				value={selectedSize}
				loading={loadingSizes}
				disabled={selectedTypeId == null}
				getOptionLabel={(o) => o.Name}
				isOptionEqualToValue={(o, v) => o.Id === v.Id}
				onChange={(_e, option) => onSizeChange(option?.Id ?? null)}
				renderInput={(params) => <TextField {...params} label="Product" />}
			/>

			<ResetFiltersButton
				onClick={onClear}
				disabled={selectedTypeId == null && selectedSizeId == null}
			/>

			<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

			<Box
				sx={{
					display: "flex",
					alignItems: "stretch",
					gap: 1.5,
					flexWrap: "wrap",
				}}
			>
				<HeaderField label="Name" value={header?.Name ?? ""} />
				<Divider orientation="vertical" flexItem />
				<HeaderField
					label="Product Type"
					value={header?.ProductTypeName ?? ""}
				/>
				<Divider orientation="vertical" flexItem />
				<HeaderField label="Section Code" value={header?.SectionCode ?? ""} />
				<Divider orientation="vertical" flexItem />
				<HeaderField label="Revision Date" value={revisionDate} />
				<Divider orientation="vertical" flexItem />
				<HeaderField
					label="Weight per Foot"
					value={header?.Weight != null ? String(header.Weight) : ""}
				/>
			</Box>
		</Paper>
	);
};
